import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import * as signSvc from '@/services/sign.service.js';
import SignaturePad from '@/components/sign/SignaturePad.jsx';

const LOGO_URL = 'https://ndemnifly.com/assets/Ndemnifly-MainLogo-Teal-scaled.png';

const CONSENT_TEXT =
  'I agree to sign this document electronically. I understand my electronic ' +
  'signature, adopted name, and the date and time of signing are legally binding ' +
  'and have the same effect as a handwritten signature under the U.S. ESIGN Act ' +
  'and UETA.';

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-bg text-text flex flex-col items-center px-4 py-10">
      <img src={LOGO_URL} alt="NDEMNIFLY" className="h-8 mb-8" />
      <div className="w-full max-w-2xl">{children}</div>
      <div className="mt-10 text-xs text-text-dim">Sign with Tower by NDEMNIFLY</div>
    </div>
  );
}

function Card({ children, className = '' }) {
  return <div className={`bg-surface border border-border rounded-card p-6 ${className}`}>{children}</div>;
}

export default function Sign() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [fatal, setFatal] = useState(null);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await signSvc.getSigner(token));
      setFatal(null);
    } catch (e) {
      setFatal(e.message || 'This signing link is not valid.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <Shell><div className="text-center text-text-muted animate-pulse">Loading…</div></Shell>;
  }
  if (fatal) {
    return <Shell><Card className="text-center"><p className="text-accent">{fatal}</p></Card></Shell>;
  }
  if (!data) return null;

  const { document: doc, signer, fields, needs_initials } = data;

  if (signer.signed || doc.status === 'completed') {
    return <Shell><ThankYou completed={doc.status === 'completed'} /></Shell>;
  }
  if (signer.otp_required && !signer.otp_verified) {
    return <Shell><OtpGate token={token} onVerified={load} /></Shell>;
  }
  if (!signer.can_sign) {
    return <Shell><Waiting /></Shell>;
  }
  return (
    <Shell>
      <SignForm token={token} doc={doc} signer={signer} fields={fields} needsInitials={needs_initials} onSigned={load} />
    </Shell>
  );
}

function ThankYou({ completed }) {
  return (
    <Card className="text-center">
      <div className="w-12 h-12 rounded-full bg-success-soft text-success flex items-center justify-center mx-auto mb-4 text-2xl">✓</div>
      <h1 className="text-lg font-semibold mb-2">Thank you — your signature is recorded.</h1>
      <p className="text-sm text-text-muted">
        {completed
          ? 'All parties have signed. A copy will be sent to you.'
          : 'We will notify the remaining signers. You may close this window.'}
      </p>
    </Card>
  );
}

function Waiting() {
  return (
    <Card className="text-center">
      <h1 className="text-lg font-semibold mb-2">Almost there</h1>
      <p className="text-sm text-text-muted">This document is waiting on an earlier signer. We'll email you when it's your turn.</p>
    </Card>
  );
}

function OtpGate({ token, onVerified }) {
  const [code, setCode] = useState('');
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (busy || !code.trim()) return;
    setBusy(true); setErr(null);
    try {
      await signSvc.verifyOtp(token, code.trim());
      onVerified();
    } catch (e2) {
      const remain = e2.body?.attempts_remaining;
      setErr(remain != null ? `${e2.message} — ${remain} attempt${remain === 1 ? '' : 's'} left` : e2.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <h1 className="text-lg font-semibold mb-1">Verify it's you</h1>
      <p className="text-sm text-text-muted mb-5">Enter the access code your broker sent you to open this document.</p>
      <form onSubmit={submit}>
        <input
          inputMode="numeric" autoFocus value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="6-digit code"
          className="w-full text-center tracking-[0.4em] text-xl px-3 py-3 border border-border rounded-lg bg-surface-strong text-text outline-none focus:border-accent"
        />
        {err && <p className="text-accent text-sm mt-3">{err}</p>}
        <button type="submit" disabled={busy || code.length < 6}
          className="w-full mt-4 bg-accent text-white font-medium py-2.5 rounded-pill disabled:opacity-40">
          {busy ? 'Verifying…' : 'Continue'}
        </button>
      </form>
    </Card>
  );
}

function SignForm({ token, doc, signer, fields, needsInitials, onSigned }) {
  const [name, setName] = useState(signer.name || '');
  const [initials, setInitials] = useState('');
  const [sigImage, setSigImage] = useState(null);
  const [initImage, setInitImage] = useState(null);
  const [consent, setConsent] = useState(false);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const ready = consent && name.trim() && sigImage && (!needsInitials || initImage);

  async function submit() {
    if (!ready || busy) return;
    setBusy(true); setErr(null);
    try {
      await signSvc.submitSignature(token, {
        esign_consent: true,
        adopted_name: name.trim(),
        adopted_initials: initials.trim() || null,
        signature_image: sigImage,
        initials_image: initImage || null,
      });
      onSigned();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <Card>
        <h1 className="text-lg font-semibold">{doc.title || 'Document'}</h1>
        <p className="text-sm text-text-muted mt-1">Review the document, then adopt your signature below.</p>
        {doc.has_fields && (
          <div className="mt-4">
            <iframe
              title="document" src={signSvc.guestPreviewUrl(token)}
              className="w-full rounded-lg border border-border bg-white" style={{ height: 420 }}
            />
            <a href={signSvc.guestPreviewUrl(token)} target="_blank" rel="noreferrer"
              className="inline-block mt-2 text-sm text-accent">Open document in a new tab ↗</a>
          </div>
        )}
      </Card>

      <Card>
        <label className="block text-sm font-medium mb-1">Full legal name</label>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your full name"
          className="w-full px-3 py-2.5 border border-border rounded-lg bg-surface-strong text-text outline-none focus:border-accent mb-5" />

        <label className="block text-sm font-medium mb-2">Signature</label>
        <SignaturePad onChange={setSigImage} />

        {needsInitials && (
          <div className="mt-5">
            <label className="block text-sm font-medium mb-1">Initials</label>
            <input value={initials} onChange={(e) => setInitials(e.target.value.toUpperCase().slice(0, 5))}
              placeholder="e.g. DJA"
              className="w-32 px-3 py-2 border border-border rounded-lg bg-surface-strong text-text outline-none focus:border-accent mb-2" />
            <SignaturePad onChange={setInitImage} placeholder="Type your initials" height={120} />
          </div>
        )}
      </Card>

      <Card>
        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 accent-[#C62D48]" />
          <span className="text-sm text-text-muted">{CONSENT_TEXT}</span>
        </label>
        {err && <p className="text-accent text-sm mt-3">{err}</p>}
        <button onClick={submit} disabled={!ready || busy}
          className="w-full mt-4 bg-accent text-white font-medium py-2.5 rounded-pill disabled:opacity-40">
          {busy ? 'Submitting…' : 'Adopt and sign'}
        </button>
      </Card>
    </div>
  );
}
